/**
 * Kumo SDK エントリ。
 *
 *   const kumo = await Kumo.init({ projectKey: 'pk_dev_…' });
 *   kumo.auth / kumo.saves / kumo.stats
 *   kumo.rooms / kumo.realtime（リアルタイムの部屋）
 *   kumo.social（profile / declareAge = 年齢の申告）（social.ts）/ kumo.sanctions（本人の制裁の確認と BAN の異議）（sanctions.ts）
 *   kumo.device（触覚・プラットフォーム・年齢レンジ）/ kumo.account（アカウント削除・エクスポート）
 *   kumo.share（X への共有・挑戦状・アプリ内ブラウザ対策）/ kumo.x（X の名前とアイコン）（share.ts）
 *   kumo.mail（ゲームのお知らせメールを止める: unsubscribe）（mail.ts）
 *   kumo.news（サービス自身のお知らせの欄 = Kumo が描く部品: mountNewsOptIn / unsubscribe）（news.ts）
 *
 * ネイティブアプリ（Capacitor・iOS / Android）では window.Capacitor を自動検出し、トークンの保存先を Keychain / Keystore に、
 * 外部ログインをネイティブの経路に切り替える（native.ts / nativeAuth.ts / nativeStore.ts）。ゲームのコードは Web と同じ。
 *
 * 各機能は独立ファイル（./<module>.ts）の `create<Module>(core)` で組み立てる。
 * マルチプレイ・ソーシャル・ネイティブのログインなどは、使うときだけ読む（lazy.ts・lazyCommerce.ts。2026-09-27 sdk-light）。
 * ここで作るのは同じ形の窓口で、ゲームのコード（kumo.rooms.join(...) など）は変わらない。
 *
 * 最初の公開で見せない機能は、返すオブジェクトからも型からも外す（一覧と理由は launchHidden.ts・founder 2026-09-27 MASTER-PLAN §1c）。
 * WHY ここに名前を並べないか（2026-09-27 appeal-tools・Quality SEC-HIDE-04）: このファイルの説明は配布物の index.d.ts に載る =
 *   隠した機能の名前を並べると「Kumo にその機能がある / 後で出る」と読める。中身のコードは残してあり、launchHidden.ts の一覧から外せば戻る。
 * kumo.sanctions（本人の制裁の確認と異議 = sanctions.ts）は常に出す（プレイヤーの BAN は残す = 誤った BAN を取り消してもらう道）
 * ランキングなどはクリエイター自身の DB と Functions で作る（テンプレートの .claude/skills/leaderboard/ の Skill）。
 */

import { KumoCore, KumoError, type KumoInitOptions } from './core.js';
import { createAuth } from './auth.js';
import { handOffPopupResult } from './authProviders.js';
import { createGameData } from './gamedata.js';
import { lazyLiveOps, lazyRealtime, lazyRooms, lazySocial } from './lazy.js';
import { lazyAds, lazyStore } from './lazyCommerce.js';
import { createDevice } from './device.js';
import { createShare, createX, redeemTransferFromUrl } from './share.js';
import { createMail } from './mail.js';
import { createNews } from './news.js';
import { createSanctions } from './sanctions.js';
import { isHiddenNow, withoutHidden } from './launchHidden.js';

export async function init(opts: KumoInitOptions) {
  const core = new KumoCore(opts);
  // 非同期の保存領域（ネイティブの Keychain / Keystore）なら、使うキーを先に読んでおく（storage.ts）。同期なら何もしない
  await core.prepareStorage();
  const auth = createAuth(core);
  // ポップアップのサインインの戻り（このページはポップアップの中）: 結果を開いた側へ渡して閉じる。
  // 共有の localStorage のセッションを触らないよう、復元もゲスト作成もしない（authProviders.ts の冒頭）
  const handedOff = handOffPopupResult();
  if (!handedOff) {
    // リダイレクト方式のサインイン / メールのリンクからの戻りなら、先に結果を取り込む（失敗してもゲームは起動する。
    // 結果とエラーはゲームが auth.completeRedirectSignIn() で受け取れる）
    // X のアプリ内ブラウザから「ブラウザで開く」で来た（#auth_transfer= / 旧名 #kumo_transfer=）なら、同じプレイヤーのセッションを受け取る（share.ts）
    const transferred = await redeemTransferFromUrl(core).catch(() => false);
    const redirected = transferred ? null : await auth.completeRedirectSignIn().catch(() => null);
    // 前回のセッションを復元。無ければ（既定で）ゲストとして開始 = ゲームはすぐ遊べる
    const resumed = transferred || redirected ? true : await core.resume();
    if (!resumed && opts.autoGuest !== false) await auth.guest();
  }

  const gamedata = createGameData(core);
  const store = lazyStore(core);
  // ネイティブアプリ: 前回の起動で付与まで届かなかったストアの取引を裏で回収する（起動を待たせない・失敗しても次回また試す）
  // 課金（features.payments）が OFF のゲーム（2026-09-27 principles-fixes・原則の監査「すべて既定 OFF」）:
  //   - SDK は機能の ON/OFF を知らない（知るには起動ごとに 1 往復増える）。そこで「ON のときだけ呼ぶ」ではなく、
  //     サーバーを呼ぶのは端末に未完了の取引があるときだけ（plugin.unfinished() が空なら 0 往復。取引は課金が ON の時代にしか生まれない）
  //     ＋ 403 feature_disabled は静かに扱う（nativeStore.ts で打ち切り、ここでも警告を出さない）ことにした。
  //   - 警告を出さない WHY: OFF は作者が選んだ状態で、プレイヤーのコンソールに Kumo の名前入りの警告を出す理由がない
  // 2026-09-29 creator/launch-money-b: 課金を隠している間（launchHidden.ts の 'payments'）は回収も呼ばない。
  //   WHY: サーバーが課金の口を閉じている（403 feature_unavailable）= 呼んでも必ず断られる往復になるだけ。取引は端末に残り、
  //   運営が開けて SDK が出し直したときの起動で回収される（nativeStore.ts は断られた取引を終わらせない）。
  //   念のため feature_unavailable（閉じた部品）も feature_disabled と同じく静かに扱う（プレイヤーのコンソールに警告を出さない）
  if (core.platform !== 'web' && core.isSignedIn && !isHiddenNow('payments')) {
    void store.recoverUnfinished().catch((e) => {
      if (e instanceof KumoError && (e.code === 'feature_disabled' || e.code === 'feature_unavailable')) return;
      console.warn('[game-backend] could not recover unfinished store transactions', e);
    });
  }
  // 共有のリンク（?ks=）から来たかの記録と挑戦状の読み込み。起動を待たせない（share.ts）。
  // ポップアップのサインインの戻り（handOffPopupResult）では数えない = 同じ人の 1 回の訪問を 2 回にしない
  const share = createShare(core);
  if (!handedOff) void share._start();
  return withoutHidden({
    auth,
    ...gamedata,
    rooms: lazyRooms(core),
    /**
     * リアルタイムの部屋（チャンネル・realtime.ts）: 部屋の中でメッセージを即座に届け合う。rooms と同じ接続を使う。
     * features.realtimeChannels を ON にしたゲームだけ。Kumo は中身を保存しない（残したいなら on('message') で受けて自分の Functions へ）
     * 音声通話（2026-09-27 core/voice・voice-sfu）: `create({ voice: true })` で作った部屋の `ch.voice.join()`（features.voice・録音しない。8 人以下は P2P・9〜100 人は中継サーバー）
     */
    realtime: lazyRealtime(core),
    store,
    device: createDevice(core),
    /**
     * アカウントの削除・エクスポート（auth.deleteAccount / exportData と同じもの）。
     * WHY 別名を置くか: App Store 5.1.1(v) / Google Play は「アプリ内でアカウント削除できること」を求める。
     * ゲームの設定画面を作る人が探しやすい場所（kumo.account.delete）にも置く
     */
    account: {
      delete: auth.deleteAccount,
      exportData: auth.exportData
    },
    ads: lazyAds(core),
    /** X への共有・挑戦状・流入の記録・アプリ内ブラウザ対策（share.ts・docs/X-AFFINITY.md）。kumo.share({...}) で投稿画面を開く */
    share,
    /** X でログインした人の名前とアイコン（本人が公開を選んだ人だけ） */
    x: createX(core),
    /**
     * ゲームのお知らせメールを止める（unsubscribe）。mail.ts。小さいので lazy にしない。
     * 表に出す口は launchHidden.ts が決める（2026-09-29 creator/mail-close: 止める口は閉じた後も残す = 同意の撤回の道を消さない）
     */
    mail: createMail(core),
    /**
     * サービス（Kumo）自身のお知らせの欄（Kumo が描く部品・founder 2026-09-27）。メールアドレスの登録画面に必ず入れる。news.ts。
     * ゲームのお知らせ（mail）とは別の同意・別の停止。小さい（DOM を数個作るだけ）ので lazy にしない
     */
    news: createNews(core),
    /**
     * 自分への制裁（BAN など）を見て異議を申し立てる（sanctions.ts）。BAN の後の起動でもすぐ使うので核に置く（小さい）。
     * 機能の切り替えの外 = 常に出す（サーバーも常に開いている）
     */
    sanctions: createSanctions(core),
    // ソーシャル・運営機能（使うときだけ読む。表に出す部分は launchHidden.ts が決める）
    ...lazySocial(core),
    ...lazyLiveOps(core),
    /** 生の API 呼び出し（SDK に未実装のエンドポイント用） */
    request: core.request.bind(core),
    core
  });
}

export type Kumo = Awaited<ReturnType<typeof init>>;
export { KumoError };
// KumoError の hint / docsUrl の形（2026-09-29 creator/core-dx2 C1）
export type { KumoErrorGuide } from './core.js';
export type { OAuthProvider, ProviderSignInOptions, ProviderSignInResult, PendingAction } from './authProviders.js';
export type { KumoPlayerExport, EmailAuthOptions } from './auth.js';
export type { SignInIndicator, SignInWaitingState } from './authWaiting.js';
// 広告の型（ads.ts）。広告（kumo.ads）を隠していた間（2026-09-29 creator/launch-money-b〜）は公開の入口から外していた
// （型の名前は配布物の index.d.ts と補完に出る = 「広告の機能がある」と読める・SEC-HIDE-04 と同じ）。
// 2026-10-19 creator/ads-open で launchHidden.ts から 'ads' を外したので戻した。また隠すときはこの 1 行も外す
export type { AdAdapter, AdShowRequest, AdShowResult, RewardedResult, InterstitialResult, ConsentInput } from './ads.js';
export type { KumoStorage, SyncStorage } from './storage.js';
export type { KumoPlatform, NativeBridge } from './native.js';
export type { HapticStyle, AgeRangeSignal } from './device.js';
export type { ShareOptions, ShareResult, SharedLink, IntentOptions, XProfile } from './share.js';
// ゲームのお知らせメールの申し込みの型（MailSubscribeOptions・MailSubscribeResult・MailStatus・MailOptIn = mail.ts）は、申し込み（kumo.mail の
// subscribe / status と kumo.auth の mailOptIn）を隠している間は公開の入口から出さない（2026-09-29 creator/mail-close・Kumo 管理の決定。
// 広告の型と同じ WHY = 型の名前は index.d.ts と補完に出る）。残す unsubscribe の結果の型はその場の形（mail.ts）なので名前は要らない。
// launchHidden.ts から 'mail' を外して出し直すときは、ここに次の 1 行を戻す:
//   export type { MailSubscribeOptions, MailSubscribeResult, MailStatus, MailOptIn } from './mail.js';
export type { NewsOptInHandle, NewsOptInOptions, NewsSubscribeResult } from './news.js';
export type { Sanction, AppealResult } from './sanctions.js';
export { detectPlatform } from './native.js';
export default { init, KumoError };
